import { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useDebounce } from 'src/hooks/Debounce';
import { SVGAssets } from '@business/assets/vectors';
import { t } from 'i18next';
import { Input } from '../Input';

interface Option {
  id: string;
  name: string;
  profilePicUrl: string;
  userId?: string;
  subscriberId?: string;
}

interface ChangeValue {
  id: string;
  name: string;
  profilePicUrl: string;
  userId?: string;
  subscriberId?: string;
}

interface Props {
  ref?: any;
  name?: string;
  control?: any;
  onChange?: (value: ChangeValue) => void;
  label?: string;
  placeholder?: string;
  isWhiteTheme?: boolean;
  options?: Option[];
  onSearch?: (search: string) => void;
  value?: string;
  isSelectOnly?: boolean;
  error?: string;
  isDisabled?: boolean;
  isFromCreatePeople?: boolean;
  profilePicUrl?: string;
  loading?: boolean;
}

export const SearchableSelectInput = ({
  loading,
  name,
  control,
  onChange,
  label,
  placeholder,
  isWhiteTheme,
  options = [],
  onSearch,
  value,
  isSelectOnly = false,
  error,
  isDisabled,
  isFromCreatePeople,
  profilePicUrl,
  ref
}: Props) => {
  const [searchText, setSearchText] = useState<string>('');
  const [open, setOpen] = useState<boolean>(false);
  const [isFromOption, setIsFromOption] = useState<boolean>(false);
  const [validationError, setValidationError] = useState<string>('');
  const [selectedProfilePicUrl, setSelectedProfilePicUrl] =
    useState<string>('');

  const isUserTypingRef = useRef(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const debouncedSearch = useDebounce(searchText, 500);

  const internalRef = useRef<HTMLInputElement>(null);

  // 👇 expose focus to parent
  useImperativeHandle(ref, () => ({
    focus: () => internalRef.current?.focus()
  }));

  useEffect(() => {
    if (!isUserTypingRef.current) {
      setSearchText(value || '');
      setValidationError('');
      // If value is set externally, consider it as from option
      if (value) {
        setIsFromOption(true);
        // Set profile picture if provided
        if (profilePicUrl) {
          setSelectedProfilePicUrl(profilePicUrl);
        }
      }
    }
  }, [value, profilePicUrl]);

  useEffect(() => {
    if (!isUserTypingRef.current) return;

    if (debouncedSearch && onSearch) {
      onSearch(debouncedSearch);
    }

    if (debouncedSearch.trim()) {
      setOpen(true);
      return;
    }

    setOpen(false);
  }, [debouncedSearch, onSearch]);

  useEffect(() => {
    if (!isUserTypingRef.current) return;

    if (loading) {
      setOpen(true);
      return;
    }

    if (debouncedSearch.trim()) {
      setOpen(options.length > 0 || isSelectOnly);
    } else {
      setOpen(false);
    }
  }, [loading, options.length, isSelectOnly, debouncedSearch]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
        isUserTypingRef.current = false;

        // Validate on blur if isSelectOnly is true
        if (isSelectOnly && searchText && !isFromOption) {
          setValidationError('Please select one from dropdown');
        }
      }
    };

    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isSelectOnly, searchText, isFromOption]);

  const renderInput = (field?: any) => (
    <>
      <Input
        ref={internalRef}
        {...field}
        disabled={isDisabled}
        label={label}
        placeholder={placeholder}
        isWhiteTheme={isWhiteTheme}
        inputWrapperClassName={`${isFromCreatePeople ? '!px-2 !py-2' : ''} ${isDisabled ? 'opacity-60 cursor-not-allowed' : ''}`}
        inputClassName={isDisabled ? 'bg-gray-100 cursor-not-allowed' : ''}
        startDecorator={
          isFromOption ? (
            selectedProfilePicUrl ? (
              <img
                src={selectedProfilePicUrl}
                alt="profile"
                className="w-5 h-5 rounded-full object-cover"
              />
            ) : (
              <SVGAssets.Avatar className="w-5 h-5" />
            )
          ) : null
        }
        value={searchText}
        onChange={e => {
          isUserTypingRef.current = true;
          setIsFromOption(false);
          setSelectedProfilePicUrl('');
          setValidationError('');
          const val = e.target.value;
          setSearchText(val);

          if (!isSelectOnly) {
            field?.onChange?.(val);
            onChange?.({
              id: '',
              name: val,
              profilePicUrl: '',
              userId: '',
              subscriberId: ''
            });
          }
        }}
        onFocus={() => {
          isUserTypingRef.current = true;
          setValidationError('');

          onSearch?.(searchText);
          setOpen(true);
        }}
      />

      {(validationError || error) && (
        <p className="text-red-500 text-xs mt-1">{validationError || error}</p>
      )}

      {open && (
        <ul className="absolute z-[10005] w-full bg-white border rounded-md shadow-lg mt-1 max-h-52 overflow-auto">
          {loading ? (
            <li className="px-3 py-2 text-sm text-gray-500 text-center">
              {t('recipesTab.loading')}
            </li>
          ) : options.length > 0 ? (
            options.map(item => (
              <li
                key={item.id}
                className="px-3 py-2 cursor-pointer hover:bg-gray-100 text-sm flex items-center gap-2 text-black"
                onMouseDown={e => {
                  e.preventDefault();
                  isUserTypingRef.current = false;
                  setIsFromOption(true);
                  setSelectedProfilePicUrl(item?.profilePicUrl || '');
                  setValidationError('');
                  setSearchText(item?.name);
                  field?.onChange?.(item?.name);
                  onChange?.({
                    id: item?.id,
                    name: item?.name,
                    profilePicUrl: item?.profilePicUrl,
                    userId: item?.userId,
                    subscriberId: item?.subscriberId
                  });
                  setOpen(false);
                }}>
                {item?.profilePicUrl ? (
                  <img
                    src={item.profilePicUrl}
                    alt={item?.name}
                    className="w-5 h-5 rounded-full"
                  />
                ) : (
                  <SVGAssets.Avatar className="w-5 h-5" />
                )}
                <span>{item?.name}</span>
              </li>
            ))
          ) : (
            isSelectOnly && (
              <li className="px-3 py-2 text-sm text-gray-500 text-center">
                {t('noOptionsFound')}
              </li>
            )
          )}
        </ul>
      )}
    </>
  );

  return (
    <div ref={wrapperRef} className="relative">
      {control && name ? (
        <Controller
          name={name}
          control={control}
          render={({ field }) => renderInput(field)}
        />
      ) : (
        renderInput()
      )}
    </div>
  );
};
